/**
 * @file   src\Pages\HardwareDetails.tsx
 * @brief  HardwareDetails page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import SmallLoader from '../components/SmallLoader';
import axios from '../config/axiosConfig';
import { AppContextType } from '../interfaces/generalInterface';
import { HardwareItem } from '../interfaces/hardwareInterface';
import { HardwareApi } from '../utils/apiUrls';
import { pageURLs } from '../utils/pageUrls';
import SyncIcon from '../assets/images/icons/syncicon';
import { Container, Tab, Tabs, Table, Badge, Button, useEffect, useOutletContext, useState, useLocation, Pagination, React } from '../utils/thirdpartyComponents';
import { DashBoardApi } from "../utils/apiUrls";
import { formatDateTimeToUs } from '../utils/helper';
import { userType } from '../utils/enums';

const HardwareDetails = (props: any) => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const [currentPage, setCurrentPage] = useState(query.get('page') ? parseInt(query.get('page') as string, 10) : 1);
    //To access hardware type clicked
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const state = location.state || {};
    const { type } = state;

    // State to display hardware details
    const [hardwareDetails, setHardwareDetails] = useState<HardwareItem[]>([])

    const [status, setStatus] = useState(type === "inactive" ? 0 : 1)
    const [statusLastUpdatedDate, setstatusLastUpdatedDate] = useState('')
    useEffect(() => {
        handleUpdate()
        getHardwareDetails(currentPage)

    }, [status, currentPage, type])

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Hardware Details", url: props.userType !== userType['CARWASH-OWNER'] ? `/superadmin/${pageURLs.dashboard}` : `/${pageURLs.dashboard}`, showBackButton: true, showMainMenu: true, activeLink: props.userType !== userType['CARWASH-OWNER'] ? "manage-car-wash" : "" });
    };

    const getHardwareDetails = async (pageNo: number) => {
        try {
            setLoading(true)
            const param = `?pageNo=${pageNo}&status=${status}`;
            const { data } = (props.userType !== userType['CARWASH-OWNER']) ?
                await axios.get(HardwareApi.HARDWARE_LIST + param)
                : await axios.get(HardwareApi.CARWASH_OWNER_HARDWARE_LIST + param);
            if (data?.error === 0) {
                setLoading(false)
                setstatusLastUpdatedDate(data.data.statusUpdateOn)

                if (Array.isArray(data?.data?.list)) {
                    const hardware_list: HardwareItem[] = data.data.list;
                    // Check if location_list is empty
                    if (hardware_list.length > 0) {
                        setHardwareDetails(hardware_list);
                        setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);

                    } else {
                        console.log('Hardware list is empty');
                        setHardwareDetails([]);
                    }
                }
            } else {
                console.log('Hardware list is not available or is not an array');
            }

        } catch (error: any) {
            setLoading(false)
            console.error('Error fetching hardware list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
        finally {
            setLoading(false); // Hide loader
        }
    }


    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const onTabChange = (type: any) => {
        // To handle tab change
        setCurrentPage(1)
        if (type === "active")
            setStatus(1)
        else
            setStatus(0)

    }

    //Synch Dashboard Data
    const synchDeviceStatus = async () => {
        try {
            setLoading(true)
            const apiUrl = DashBoardApi.SYNCH_DEVICE_STATUS_V2;
            const { data } = await axios.post(apiUrl);
            if (data?.error === 0) {
                getHardwareDetails(1)
            } else {
                setLoading(false)
            }
        } catch (error: any) {
            console.error('Error fetching device status:', error.message);
            setLoading(false)
            //throw new Error(error.response?.data?.message || error.message);
        }
    };

    return (

        <div className='hardware-data-container'>
            <Container>
                <div className="cw-main-tab">
                    <Tabs
                        defaultActiveKey={type}
                        id="hardware-tab"
                        className="cw-tab-title justify-content-end"
                        onSelect={(k) => onTabChange(k)}

                    >
                        <Tab eventKey="active" title="Active">
                            {props.userType !== userType['CARWASH-OWNER'] ?
                                <div className='sync-sec d-flex justify-content-end align-items-center mb-3'>
                                    {statusLastUpdatedDate && <p className='f-semi'>Last updated on {formatDateTimeToUs(statusLastUpdatedDate)}</p>}
                                    <Button title="Sync now" variant="secondary" className="d-flex align-items-center secondary-btn btn-details" onClick={() => synchDeviceStatus()}>
                                        Sync now <SyncIcon />
                                    </Button>
                                </div> : null}
                            <div className="cw-table-wrap">
                                <Table responsive className='m-0'>
                                    <thead>
                                        <tr>
                                            {props.userType !== userType['CARWASH-OWNER'] ? <th>Company Name</th> : null}
                                            <th>Location Name</th>
                                            <th>Bay Type</th>
                                            <th>Bay Number</th>
                                            <th>Hardware Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {hardwareDetails && hardwareDetails.length > 0 ? (
                                            hardwareDetails.filter(item => item.HardwareStatus === 1).map((item, index) => (
                                                <tr key={index}>
                                                    {props.userType !== userType['CARWASH-OWNER'] ? <td>{item.companyName}</td> : null}
                                                    <td>{item.locationName}</td>
                                                    <td>{item.BayTypeID === 1 ? "Tunnel" : "Bay"}</td>
                                                    <td>{item.BayNumber}
                                                    </td>

                                                    <td><Badge className='active'>{item.HardwareStatus === 0 ? "Inactive" : "Active"}</Badge>
                                                        <br />
                                                        <p><span className='title'>Device:</span>{item.DeviceName}</p>
                                                    </td>
                                                </tr>
                                            ))) : (
                                            <tr>
                                                <td style={{ textAlign: 'center' }} colSpan={5} >No data found.</td>
                                            </tr>
                                        )}

                                    </tbody>
                                </Table>

                                {hardwareDetails.length ?
                                    <div className="mt-3 d-flex justify-content-end">
                                        <Pagination>
                                            <Pagination.First
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(1)}
                                            />
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />
                                            {getVisiblePageNumbers().map((pageNumber) => (
                                                <Pagination.Item
                                                    key={pageNumber}
                                                    active={pageNumber === currentPage}
                                                    onClick={() => handlePageChange(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </Pagination.Item>
                                            ))}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                            <Pagination.Last
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(totalPages)}
                                            />
                                        </Pagination>
                                    </div> : null}
                            </div>
                        </Tab>
                        <Tab eventKey="inactive" title="Inactive">
                            {props.userType !== userType['CARWASH-OWNER'] ?
                                <div className='sync-sec d-flex justify-content-end align-items-center mb-3'>
                                    {statusLastUpdatedDate && <p className='f-semi'>Last updated on {formatDateTimeToUs(statusLastUpdatedDate)}</p>}
                                    <Button title='Sync now' variant="secondary" className="d-flex align-items-center secondary-btn btn-details" onClick={() => synchDeviceStatus()}>
                                        Sync now <SyncIcon />
                                    </Button>
                                </div> : null}
                            <div className="cw-table-wrap">
                                <Table responsive className='m-0'>
                                    <thead>
                                        <tr>
                                            {props.userType !== userType['CARWASH-OWNER'] ? <th>Company Name</th> : null}
                                            <th>Location Name</th>
                                            <th>Bay Number</th>
                                            <th>Bay Type</th>
                                            <th>Hardware Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {hardwareDetails && hardwareDetails.length > 0 ? (
                                            hardwareDetails.filter(item => item.HardwareStatus === 0).map((item) => (
                                                <tr key={item.DeviceID}>
                                                    {props.userType !== userType['CARWASH-OWNER'] ? <td>{item.companyName}</td> : null}
                                                    <td>{item.locationName}</td>
                                                    <td>{item.BayName}<br />
                                                        <p><span className='title'>Device:</span>{item.DeviceName}</p>
                                                    </td>
                                                    <td>{item.BayTypeID === 1 ? "Tunnel" : "Automatic"}</td>
                                                    <td><Badge className='inactive'>{item.HardwareStatus === 0 ? "Inactive" : "Active"}</Badge></td>
                                                </tr>
                                            ))) : (
                                            <tr>
                                                <td style={{ textAlign: 'center' }} colSpan={5} >No data found.</td>
                                            </tr>
                                        )}

                                    </tbody>
                                </Table>

                                {hardwareDetails.length ?
                                    <div className="mt-3 d-flex justify-content-end">
                                        <Pagination>
                                            <Pagination.First
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(1)}
                                            />
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />
                                            {getVisiblePageNumbers().map((pageNumber) => (
                                                <Pagination.Item
                                                    key={pageNumber}
                                                    active={pageNumber === currentPage}
                                                    onClick={() => handlePageChange(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </Pagination.Item>
                                            ))}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                            <Pagination.Last
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(totalPages)}
                                            />
                                        </Pagination>
                                    </div> : null}
                            </div>
                        </Tab>
                    </Tabs>
                </div>
            </Container>
            {loading && <SmallLoader />}
        </div>
    )
}

export default HardwareDetails;
